import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from "@/components/ui/button";
import { DraggableModal } from "@/components/ui/draggable-modal";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Camera, Video, X, Check, Upload, MapPin, AlertTriangle, Loader2, RotateCcw, Trash2 } from 'lucide-react';
import { toast } from "sonner";
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function MediaCapture({
  isOpen,
  onClose,
  parkId,
  personnelId,
  taskId,
  taskName,
  date,
  mediaType = "photo", // "photo", "video", "both"
  maxVideos = 3,
  maxPhotos = 5,
  onUploadComplete
}) {
  // State
  const [mode, setMode] = useState(mediaType === "video" ? "video" : "photo");
  const [stream, setStream] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordedTime, setRecordedTime] = useState(0);
  const [capturedMedia, setCapturedMedia] = useState([]); // [{type, blob, url, uploaded}]
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [gpsStatus, setGpsStatus] = useState({ loading: true, lat: null, lng: null, error: null });
  const [facingMode, setFacingMode] = useState("environment"); // "user" veya "environment"
  // Kamera açılamazsa (izin reddi / cihazda kamera yok / güvensiz bağlam) dosya
  // seçme fallback'i sunulur. Bu bir test/güvenlik bypass'ı DEĞİLDİR: seçilen
  // dosya da aynı /api/media/upload yolundan geçer ve backend'de magic-byte,
  // boyut, MIME, GPS/geofence, token ve ACL kontrollerine tabidir.
  const [cameraError, setCameraError] = useState(false);
  
  // Refs
  const videoRef = useRef(null);
  const fileInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const recordingTimerRef = useRef(null);
  
  // GPS konumu al
  useEffect(() => {
    if (isOpen) {
      setGpsStatus({ loading: true, lat: null, lng: null, error: null });
      
      if (!navigator.geolocation) {
        setGpsStatus({ loading: false, lat: null, lng: null, error: "Tarayıcınız konum özelliğini desteklemiyor" });
        return;
      }
      
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setGpsStatus({
            loading: false,
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            error: null
          });
        },
        (error) => {
          let errorMessage = "Konum alınamadı";
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = "Konum izni reddedildi. Lütfen tarayıcı ayarlarından izin verin.";
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = "Konum bilgisi kullanılamıyor";
              break;
            case error.TIMEOUT:
              errorMessage = "Konum isteği zaman aşımına uğradı";
              break;
            default:
              errorMessage = "Bilinmeyen konum hatası";
          }
          setGpsStatus({ loading: false, lat: null, lng: null, error: errorMessage });
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    }
  }, [isOpen]);
  
  // Kamera başlat
  const startCamera = useCallback(async () => {
    try {
      const constraints = {
        video: {
          facingMode: facingMode,
          width: { ideal: 1280 },
          height: { ideal: 720 }
        },
        audio: mode === "video"
      };
      
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error("getUserMedia yok");
      }
      const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
      setStream(mediaStream);
      setCameraError(false);

      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
    } catch (err) {
      console.error("Kamera erişim hatası:", err);
      // Kamera yoksa/izin reddedildiyse: beyaz-ekran/sonsuz-spinner YOK — dosya
      // seçme fallback'ine düş. Kullanıcıya net bilgi ver.
      setCameraError(true);
      toast.error("Kamera açılamadı — dosyadan seçebilirsiniz.");
    }
  }, [facingMode, mode]);

  // Dosya seçme fallback'i (kamera yoksa / erişilebilirlik / masaüstü).
  const ALLOWED_PHOTO = ["image/jpeg", "image/png", "image/webp"];
  const ALLOWED_VIDEO = ["video/mp4", "video/webm", "video/quicktime"];
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (e.target) e.target.value = ""; // aynı dosya tekrar seçilebilsin
    for (const file of files) {
      const isPhoto = mode === "photo";
      const allowed = isPhoto ? ALLOWED_PHOTO : ALLOWED_VIDEO;
      // İstemci ön-kontrolü (backend magic-byte ASIL kapıdır — bu yalnız UX).
      if (!allowed.includes(file.type)) {
        toast.error(`Geçersiz dosya türü: ${file.name || file.type}. İzin verilen: ${isPhoto ? "JPEG/PNG/WEBP" : "MP4/WEBM/MOV"}`);
        continue;
      }
      const url = URL.createObjectURL(file);
      setCapturedMedia((prev) => [...prev, { type: isPhoto ? "photo" : "video", blob: file, url, uploaded: false, name: file.name }]);
    }
  };
  
  // Kamera durdur
  const stopCamera = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
    }
  }, [stream]);
  
  // Dialog açıldığında kamera başlat
  useEffect(() => {
    if (isOpen) {
      startCamera();
    } else {
      stopCamera();
      setCapturedMedia([]);
      setRecordedTime(0);
      setIsRecording(false);
    }
    
    return () => stopCamera();
  }, [isOpen, startCamera, stopCamera]);
  
  // Mod değiştiğinde kamerayı yeniden başlat
  useEffect(() => {
    if (isOpen && stream) {
      stopCamera();
      setTimeout(() => startCamera(), 100);
    }
  }, [mode]);
  
  // Fotoğraf çek
  const capturePhoto = () => {
    if (!videoRef.current) return;
    
    const canvas = document.createElement('canvas');
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(videoRef.current, 0, 0);
    
    canvas.toBlob((blob) => {
      const url = URL.createObjectURL(blob);
      setCapturedMedia(prev => [...prev, { type: 'photo', blob, url, uploaded: false }]);
      toast.success("Fotoğraf çekildi!");
    }, 'image/jpeg', 0.9);
  };
  
  // Video kaydet - başlat
  const startRecording = () => {
    if (!stream) return;
    
    chunksRef.current = [];
    const options = { mimeType: 'video/webm;codecs=vp9' };
    
    try {
      mediaRecorderRef.current = new MediaRecorder(stream, options);
    } catch (e) {
      // Fallback
      mediaRecorderRef.current = new MediaRecorder(stream);
    }
    
    mediaRecorderRef.current.ondataavailable = (e) => {
      if (e.data.size > 0) {
        chunksRef.current.push(e.data);
      }
    };
    
    mediaRecorderRef.current.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: 'video/webm' });
      const url = URL.createObjectURL(blob);
      setCapturedMedia(prev => [...prev, { 
        type: 'video', 
        blob, 
        url, 
        uploaded: false,
        duration: recordedTime
      }]);
      setRecordedTime(0);
      toast.success("Video kaydedildi!");
    };
    
    mediaRecorderRef.current.start(100);
    setIsRecording(true);
    
    // Timer başlat
    recordingTimerRef.current = setInterval(() => {
      setRecordedTime(prev => {
        if (prev >= 20) {
          stopRecording();
          return 20;
        }
        return prev + 1;
      });
    }, 1000);
  };
  
  // Video kaydet - durdur
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
    }
  };
  
  // Medyayı sil
  const removeMedia = (index) => {
    setCapturedMedia(prev => {
      const newMedia = [...prev];
      URL.revokeObjectURL(newMedia[index].url);
      newMedia.splice(index, 1);
      return newMedia;
    });
  };
  
  // Kamera çevir
  const flipCamera = () => {
    setFacingMode(prev => prev === "environment" ? "user" : "environment");
  };
  
  // Tüm medyaları yükle
  const uploadAllMedia = async () => {
    if (!gpsStatus.lat || !gpsStatus.lng) {
      toast.error("GPS konumu alınamadı. Yükleme yapılamaz.");
      return;
    }
    
    const unuploadedMedia = capturedMedia.filter(m => !m.uploaded);
    if (unuploadedMedia.length === 0) {
      toast.info("Yüklenecek medya yok");
      return;
    }
    
    setIsUploading(true);
    setUploadProgress(0);
    
    let uploadedCount = 0;
    
    for (let i = 0; i < capturedMedia.length; i++) {
      const media = capturedMedia[i];
      if (media.uploaded) continue;
      
      try {
        const formData = new FormData();
        const filename = media.type === 'photo' ? `photo_${Date.now()}.jpg` : `video_${Date.now()}.webm`;
        formData.append('file', media.blob, filename);
        formData.append('park_id', parkId);
        formData.append('personnel_id', personnelId);
        formData.append('task_id', taskId);
        formData.append('date', date);
        formData.append('media_type', media.type);
        formData.append('latitude', gpsStatus.lat);
        formData.append('longitude', gpsStatus.lng);
        if (media.duration) {
          formData.append('duration', media.duration);
        }
        
        const response = await axios.post(`${API}/api/media/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(Math.round((uploadedCount / unuploadedMedia.length * 100) + (percentCompleted / unuploadedMedia.length)));
          }
        });
        
        if (response.data.success) {
          setCapturedMedia(prev => {
            const newMedia = [...prev];
            newMedia[i] = { ...newMedia[i], uploaded: true, serverUrl: response.data.file_url };
            return newMedia;
          });
          uploadedCount++;
        }
      } catch (error) {
        console.error("Yükleme hatası:", error);
        const errorMessage = error.response?.data?.detail || "Yükleme başarısız";
        toast.error(`${media.type === 'photo' ? 'Fotoğraf' : 'Video'} yüklenemedi: ${errorMessage}`);
      }
    }
    
    setIsUploading(false);
    setUploadProgress(100);
    
    if (uploadedCount > 0) {
      toast.success(`${uploadedCount} medya başarıyla yüklendi`);
      if (onUploadComplete) {
        onUploadComplete(capturedMedia.filter(m => m.uploaded));
      }
    }
  };
  
  // Kalan medya sayısı
  const remainingPhotos = maxPhotos - capturedMedia.filter(m => m.type === 'photo').length;
  const remainingVideos = maxVideos - capturedMedia.filter(m => m.type === 'video').length;
  
  return (
    <DraggableModal
      isOpen={isOpen}
      onClose={onClose}
      title={<span className="flex items-center gap-2">{mode === "photo" ? <Camera className="h-5 w-5" /> : <Video className="h-5 w-5" />}{taskName} - Medya Ekle</span>}
      subtitle="Görevi tamamlamak için fotoğraf veya video çekin"
      maxWidth="max-w-2xl"
    >
        {/* GPS Durumu */}
        <div className={`flex items-center gap-2 p-2 rounded-lg text-sm ${
          gpsStatus.loading ? 'bg-gray-100' :
          gpsStatus.error ? 'bg-red-100 text-red-800' :
          'bg-green-100 text-green-800'
        }`}>
          <MapPin className="h-4 w-4" />
          {gpsStatus.loading ? (
            <><Loader2 className="h-4 w-4 animate-spin" /> Konum alınıyor...</>
          ) : gpsStatus.error ? (
            <><AlertTriangle className="h-4 w-4" /> {gpsStatus.error}</>
          ) : (
            <>✓ Konum doğrulandı ({gpsStatus.lat?.toFixed(4)}, {gpsStatus.lng?.toFixed(4)})</>
          )}
        </div>
        
        {/* Mod Seçimi */}
        {mediaType === "both" && (
          <div className="flex gap-2 justify-center mt-4">
            <Button
              variant={mode === "photo" ? "default" : "outline"}
              onClick={() => setMode("photo")}
              disabled={isRecording}
            >
              <Camera className="h-4 w-4 mr-2" />
              Fotoğraf ({remainingPhotos} kaldı)
            </Button>
            <Button
              variant={mode === "video" ? "default" : "outline"}
              onClick={() => setMode("video")}
              disabled={isRecording}
            >
              <Video className="h-4 w-4 mr-2" />
              Video ({remainingVideos} kaldı)
            </Button>
          </div>
        )}
        
        {/* Kamera Görünümü */}
        <div className="relative bg-black rounded-lg overflow-hidden aspect-video mt-4">
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="w-full h-full object-cover"
          />
          
          {/* Kayıt göstergesi */}
          {isRecording && (
            <div className="absolute top-4 left-4 flex items-center gap-2 bg-red-600 text-white px-3 py-1 rounded-full">
              <span className="w-3 h-3 bg-white rounded-full animate-pulse" />
              <span className="font-mono">{recordedTime}s / 20s</span>
            </div>
          )}
          
          {/* Kamera çevir butonu */}
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-4 right-4 bg-black/50 text-white hover:bg-black/70"
            onClick={flipCamera}
          >
            <RotateCcw className="h-5 w-5" />
          </Button>
        </div>
        
        {/* Kontrol Butonları */}
        <div className="flex justify-center gap-4 mt-4">
          {mode === "photo" ? (
            <Button
              size="lg"
              className="rounded-full w-16 h-16"
              onClick={capturePhoto}
              disabled={!stream || remainingPhotos <= 0 || gpsStatus.loading || gpsStatus.error}
            >
              <Camera className="h-8 w-8" />
            </Button>
          ) : (
            <Button
              size="lg"
              className={`rounded-full w-16 h-16 ${isRecording ? 'bg-red-600 hover:bg-red-700' : ''}`}
              onClick={isRecording ? stopRecording : startRecording}
              disabled={!stream || (!isRecording && remainingVideos <= 0) || gpsStatus.loading || gpsStatus.error}
            >
              {isRecording ? (
                <div className="w-6 h-6 bg-white rounded" />
              ) : (
                <Video className="h-8 w-8" />
              )}
            </Button>
          )}

          {/* Dosya seçme fallback'i — kamera yoksa/erişilemezse veya alternatif.
              GPS yine zorunlu; dosya backend magic-byte/ACL kapısından geçer. */}
          <input
            ref={fileInputRef}
            type="file"
            data-testid="media-file-fallback"
            accept={mode === "photo" ? "image/jpeg,image/png,image/webp" : "video/mp4,video/webm,video/quicktime"}
            multiple
            className="hidden"
            onChange={handleFileSelect}
            disabled={gpsStatus.loading || gpsStatus.error || (mode === "photo" ? remainingPhotos <= 0 : remainingVideos <= 0)}
          />
          <Button
            size="lg"
            variant={cameraError ? "default" : "outline"}
            className="rounded-full h-16 px-6"
            data-testid="media-file-fallback-btn"
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
            disabled={gpsStatus.loading || gpsStatus.error || (mode === "photo" ? remainingPhotos <= 0 : remainingVideos <= 0)}
          >
            <Upload className="h-6 w-6 mr-2" /> Dosya Seç
          </Button>
        </div>
        {cameraError && (
          <p className="text-center text-sm text-amber-700 mt-2">
            Kamera açılamadı. {mode === "photo" ? "Fotoğrafı" : "Videoyu"} dosyadan seçip yükleyebilirsiniz.
          </p>
        )}
        
        {/* Video kayıt ilerleme çubuğu */}
        {isRecording && (
          <Progress value={(recordedTime / 20) * 100} className="h-2 mt-4" />
        )}
        
        {/* Çekilen Medyalar */}
        {capturedMedia.length > 0 && (
          <div className="space-y-2 mt-4">
            <h4 className="font-medium text-sm">Çekilen Medyalar ({capturedMedia.length})</h4>
            <div className="grid grid-cols-4 gap-2">
              {capturedMedia.map((media, index) => (
                <div key={index} className="relative group">
                  {media.type === 'photo' ? (
                    <img src={media.url} alt={`Fotoğraf ${index + 1}`} className="w-full aspect-square object-cover rounded-lg" />
                  ) : (
                    <video src={media.url} className="w-full aspect-square object-cover rounded-lg" />
                  )}
                  
                  {/* Yüklendi badge */}
                  {media.uploaded && (
                    <Badge className="absolute top-1 left-1 bg-green-500 text-xs">
                      <Check className="h-3 w-3 mr-1" /> Yüklendi
                    </Badge>
                  )}
                  
                  {/* Video badge */}
                  {media.type === 'video' && (
                    <Badge variant="secondary" className="absolute bottom-1 left-1 text-xs">
                      <Video className="h-3 w-3 mr-1" /> {media.duration}s
                    </Badge>
                  )}
                  
                  {/* Silme butonu */}
                  {!media.uploaded && (
                    <Button
                      variant="destructive"
                      size="icon"
                      className="absolute top-1 right-1 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={() => removeMedia(index)}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Yükleme İlerleme */}
        {isUploading && (
          <div className="space-y-2 mt-4">
            <div className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm">Yükleniyor... %{uploadProgress}</span>
            </div>
            <Progress value={uploadProgress} />
          </div>
        )}
        
        <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
          <Button variant="outline" onClick={onClose}>
            İptal
          </Button>
          <Button 
            onClick={uploadAllMedia}
            disabled={capturedMedia.filter(m => !m.uploaded).length === 0 || isUploading || gpsStatus.loading || gpsStatus.error}
          >
            <Upload className="h-4 w-4 mr-2" />
            {isUploading ? 'Yükleniyor...' : `Yükle (${capturedMedia.filter(m => !m.uploaded).length})`}
          </Button>
        </div>
    </DraggableModal>
  );
}
